import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm } from 'redux-form';
import Button from '../../components/ui/Button/Button';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import api from '../../../api';
import './ImportCompaniesForm.scss';

const ImportCompaniesForm = () => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const { t } = useTranslation();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError(t('pleaseSelectValidExcelFile'));
        setSelectedFile(null);
        event.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setMessage('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!selectedFile) {
      setError(t('selectFileToUpload'));
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.companies.importInsuranceCompaniesFromXlsx(selectedFile);
      setMessage(response.message || t('importCompletedSuccessfully'));
      setSelectedFile(null);
      // Reset file input
      event.target.reset();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t('importFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  return (
    <div className="import-companies-form-container">
      <h3 className="form-title">{t("importInsuranceCompanies")}</h3>
      
      <form onSubmit={handleSubmit} className="import-form">
        <div className="file-upload-section">
          <label htmlFor="file-upload" className="file-upload-label">
            <div className="file-upload-content">
              <CloudUploadIcon className="file-icon" />
              <span className="upload-text">
                {selectedFile ? selectedFile.name : t("chooseFile")}
              </span>
              <span className="file-hint">{t("supportedFormats")}: {t("excelFormats")}</span>
            </div>
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="file-input"
            disabled={loading}
            aria-label={t("chooseFile")}
            title={t("chooseFile")}
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {message && (
          <div className="success-message">
            {message}
          </div>
        )}

        <div className="form-footer">
          <Button
            loading={loading}
            text={loading ? t("processingFile") : t("importButton")}
            className="btn import-btn"
            type="submit"
            disabled={!selectedFile || loading}
          />
        </div>
      </form>

      <div className="import-info">
        <button 
          type="button" 
          className="instructions-toggle"
          onClick={toggleInstructions}
        >
          <h4>{t("importInstructions")}</h4>
          {showInstructions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </button>
        
        {showInstructions && (
          <ul className="instructions-list">
            <li>{t("importInstruction1")}</li>
            <li>{t("importInstruction2")}</li>
            <li>{t("importInstruction3")}</li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default compose(
  reduxForm({ form: "ImportCompaniesForm" })
)(ImportCompaniesForm);
